import { api } from "@/lib/api";
import type { Page } from "@/features/users/api";
import type { SlaInfo } from "./sla";

export type TicketStatus =
  | "open"
  | "assigned"
  | "in_progress"
  | "waiting_customer"
  | "resolved"
  | "closed"
  | "cancelled";

export type TicketPriority = "low" | "medium" | "high" | "critical";
export type NoteVisibility = "internal" | "public";

export type CallType = "amc" | "one_time" | "paid" | "free";

export interface TicketListItem {
  id: string;
  ticket_number: string;
  title: string;
  status: TicketStatus;
  priority: TicketPriority;
  customer_id: string;
  customer_name: string;
  site_id: string | null;
  site_name: string | null;
  category_name: string | null;
  location_name: string | null;
  assigned_engineer_id: string | null;
  assigned_engineer_name: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  reopened_count: number;
  call_type: CallType | null;
  call_amount: string | null;
  call_remark: string | null;
  tags: string[];
  sla: SlaInfo;
}

export interface TicketNote {
  id: string;
  body: string;
  visibility: NoteVisibility;
  author_id: string;
  author_name: string;
  created_at: string;
}

export interface TicketEvent {
  id: string;
  event_type: string;
  from_status: TicketStatus | null;
  to_status: TicketStatus | null;
  reason: string | null;
  actor_name: string | null;
  detail: string | null;
  created_at: string;
}

export interface TicketAttachment {
  id: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  kind: string;
  visibility: NoteVisibility;
  uploaded_by_name: string;
  created_at: string;
}

export interface TicketDetail extends TicketListItem {
  description: string;
  source: string;
  created_by_name: string;
  reporter_name: string | null;
  reporter_email: string | null;
  reporter_phone: string | null;
  reported_by_contact_id: string | null;
  resolved_at: string | null;
  notes: TicketNote[];
  events: TicketEvent[];
  attachments: TicketAttachment[];
  available_actions: string[];
}

export interface TicketCategory {
  id: string;
  code: string;
  name: string;
  description: string | null;
  default_priority: TicketPriority | null;
}

export type TicketSortBy = "created_at" | "updated_at" | "priority" | "sla";

export interface TicketFilters {
  page?: number;
  page_size?: number;
  search?: string;
  status?: TicketStatus[];
  priority?: TicketPriority[];
  customer_id?: string;
  assigned_engineer_id?: string;
  category_id?: string;
  service_type?: string;
  tags?: string[];
  overdue?: boolean;
  unassigned?: boolean;
  created_from?: string;
  created_to?: string;
  sort_by?: TicketSortBy;
  sort_dir?: "asc" | "desc";
}

function qs(filters: TicketFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === "") continue;
    // status and priority repeat the key rather than joining with commas —
    // FastAPI parses repeated params into a list.
    if (Array.isArray(value)) {
      for (const v of value) params.append(key, String(v));
    } else {
      params.set(key, String(value));
    }
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

export function listTickets(filters: TicketFilters = {}): Promise<Page<TicketListItem>> {
  return api.get<Page<TicketListItem>>(`/tickets${qs(filters)}`);
}

export function getTicket(id: string): Promise<TicketDetail> {
  return api.get<TicketDetail>(`/tickets/${id}`);
}

export function ticketCounts(): Promise<{ total: number; by_status: Record<string, number> }> {
  return api.get(`/tickets/counts`);
}

export function listCategories(): Promise<TicketCategory[]> {
  return api.get<TicketCategory[]>("/ticket-categories");
}

export interface CreateTicketBody {
  title: string;
  description: string;
  customer_id?: string | null;
  location_id?: string | null;
  category_id?: string | null;
  priority?: TicketPriority | null;
  source?: string;
  contact_id?: string | null;
  reporter_name?: string | null;
  reporter_email?: string | null;
  reporter_phone?: string | null;
}

export function createTicket(body: CreateTicketBody): Promise<TicketDetail> {
  return api.post<TicketDetail>("/tickets", body);
}

export function performAction(
  id: string,
  body: { action: string; reason?: string | null; assignee_id?: string | null },
): Promise<TicketDetail> {
  return api.post<TicketDetail>(`/tickets/${id}/actions`, body);
}

// Permanently remove a ticket. Administrator-only (enforced server-side).
export function deleteTicket(id: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/tickets/${id}`);
}

export type BulkAction =
  | "assign"
  | "reopen"
  | "add_note"
  | "change_priority"
  | "add_tags"
  | "remove_tags"
  | "delete";

export interface BulkTicketResult {
  updated: number;
  failed: number;
  errors: { ticket_id: string; error: string }[];
}

export function bulkTicketAction(body: {
  ticket_ids: string[];
  action: BulkAction;
  assignee_id?: string | null;
  note?: string | null;
  reason?: string | null;
  priority?: TicketPriority | null;
  tags?: string[] | null;
}): Promise<BulkTicketResult> {
  return api.post<BulkTicketResult>("/tickets/bulk", body);
}

export function changePriority(id: string, priority: TicketPriority): Promise<TicketDetail> {
  return api.post<TicketDetail>(`/tickets/${id}/priority`, { priority });
}

export function mergeTickets(target_id: string, source_ids: string[]): Promise<TicketDetail> {
  return api.post<TicketDetail>("/tickets/merge", { target_id, source_ids });
}

export function transferSite(
  id: string,
  body: { site_id: string; assignee_id?: string | null; reason?: string | null },
): Promise<TicketDetail> {
  return api.post<TicketDetail>(`/tickets/${id}/transfer-site`, body);
}

export interface TagInfo {
  name: string;
  count: number;
}

export function listTags(): Promise<TagInfo[]> {
  return api.get<TagInfo[]>("/tags");
}

export function setCallType(
  id: string,
  body: { call_type: CallType; amount?: string | null; remark?: string | null },
): Promise<TicketDetail> {
  return api.post<TicketDetail>(`/tickets/${id}/call-type`, body);
}

export function setTicketTags(id: string, tags: string[]): Promise<TicketDetail> {
  return api.put<TicketDetail>(`/tickets/${id}/tags`, { tags });
}

export function addNote(
  id: string,
  body: { body: string; visibility: NoteVisibility },
): Promise<TicketNote> {
  return api.post<TicketNote>(`/tickets/${id}/notes`, body);
}

export function uploadAttachment(
  ticketId: string,
  file: File,
  visibility: NoteVisibility = "public",
): Promise<TicketAttachment> {
  // multipart, not JSON. apiFetch leaves FormData bodies untouched (no
  // Content-Type header) so the browser sets the multipart boundary itself.
  const form = new FormData();
  form.append("file", file);
  form.append("visibility", visibility);
  return api.upload<TicketAttachment>(`/tickets/${ticketId}/attachments`, form);
}

/**
 * Attachment download path, relative to the API base.
 *
 * Pass to downloadFile (which prepends /api/v1 and attaches the bearer token);
 * a plain anchor href cannot, since the token lives only in JS memory.
 */
export function attachmentUrl(attachmentId: string): string {
  return `/attachments/${attachmentId}`;
}

export function deleteAttachment(attachmentId: string): Promise<{ message: string }> {
  return api.delete<{ message: string }>(`/attachments/${attachmentId}`);
}
